import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, User, Calendar, Clock, FileText, CheckCircle, XCircle, AlertCircle, Mail, Tag, Download } from "lucide-react";
import { useUserQuestions } from "../../hooks/queries";
import { useDragAndDrop } from "../../hooks/useDragAndDrop";
import DraggableUserQuestion from "../../components/DraggableUserQuestion";
import { exportQuestionsToPDF, generatePDFFilename } from "../../utils/pdfExport";

export default function UserQuestions() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const hasShownToast = useRef(false);
  const [localQuestions, setLocalQuestions] = useState([]);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);

  const {
    data: questionsResponse,
    isLoading,
    error,
    refetch,
    isFetching
  } = useUserQuestions(userId, {
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const questions = questionsResponse?.data || [];
  const responseMessage = questionsResponse?.message || "";

  const handleDragReorder = ({ newItems, itemName, fromPosition, toPosition }) => {
    setLocalQuestions(newItems);
  };

  const { 
    getDragProps, 
    isDragging, 
    isDraggedOver 
  } = useDragAndDrop(localQuestions, handleDragReorder);

  useEffect(() => {
    if (questions.length > 0) {
      const questionsWithIds = questions.map((q, index) => ({
        ...q,
        dragId: q.id || `question-${index}`
      }));
      setLocalQuestions(questionsWithIds);
    }
  }, [questions]);

  const moveQuestionUp = (index) => {
    if (index > 0) {
      const newQuestions = [...localQuestions];
      const questionName = newQuestions[index].submitted_by_name;
      [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
      setLocalQuestions(newQuestions);
      
      toast.success("Question Moved Up", {
        description: `${questionName}'s question moved to position ${index}`,
        duration: 2000,
      });
    }
  };

  const moveQuestionDown = (index) => {
    if (index < localQuestions.length - 1) {
      const newQuestions = [...localQuestions];
      const questionName = newQuestions[index].submitted_by_name;
      [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
      setLocalQuestions(newQuestions);
      
      toast.success("Question Moved Down", {
        description: `${questionName}'s question moved to position ${index + 2}`,
        duration: 2000,
      });
    }
  };

  useEffect(() => {
    if (isLoading || isFetching || hasShownToast.current) return;

    if (error) {
      if (error.response?.status === 404) {
        toast.error("No Questions Found", {
          description: "This user has not submitted any questions yet.",
          duration: 4000,
        });
      } else {
        toast.error("Error Loading Questions", {
          description: error.message || "Failed to load questions. Please try again.",
          duration: 4000,
        });
      }
      hasShownToast.current = true;
    } else if (questionsResponse !== undefined) {
      toast.success("Questions Successfully Retrieved", {
        description: questions.length === 0 
          ? "This user has not submitted any questions yet." 
          : `Found ${questions.length} question(s) for this user.`,
        duration: 3000,
      });
      hasShownToast.current = true;
    }
  }, [questionsResponse, error, isLoading, isFetching, questions.length]);

  useEffect(() => {
    hasShownToast.current = false;
  }, [userId]);

  const handleGoBack = () => {
    // Check if we have navigation state indicating where to return
    const navigationState = location.state;
    
    if (navigationState?.returnTo === 'remote-calls') {
      // Return to remote calls tab in interviewer dashboard
      navigate(navigationState.returnPath || '/interviewer-dashboard', { 
        state: { activeTab: navigationState.activeTab || 'remote-calls' } 
      });
    } else {
      // Default behavior: return to slots tab
      navigate('/interviewer-dashboard', { state: { activeTab: 'slots' } });
    }
  };

  const handleRefetch = () => {
    hasShownToast.current = false;
    refetch();
  };

  const handleDownloadPDF = async () => {
    try {
      if (localQuestions.length === 0) {
        return;
      }

      setIsDownloadingPDF(true);

      const firstQuestion = localQuestions[0];
      const userName = firstQuestion?.submitted_by_name || '';
      const filename = generatePDFFilename(userId, userName);
      
      await exportQuestionsToPDF(localQuestions, filename);
      
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <button
          onClick={handleGoBack}
          className="flex items-center text-muted-foreground hover:text-primary transition-colors mb-4"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </button>
        
        <div className="animate-pulse">
          <div className="h-8 bg-secondary rounded-lg w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 bg-secondary rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background p-6">
        <button
          onClick={handleGoBack}
          className="flex items-center text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </button>

        <div className="text-center py-12">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-primary" size={32} />
          </div>
          <h3 className="text-xl font-semibold text-foreground mb-2">
            {error.response?.status === 404 ? "No Questions Found" : "Error Loading Questions"}
          </h3>
          <p className="text-muted-foreground mb-6">
            {error.response?.status === 404 
              ? "This user has not submitted any questions yet." 
              : "Failed to load questions. Please try again."}
          </p>
          <div className="space-x-4">
            <button 
              onClick={handleRefetch} 
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              disabled={isFetching}
            >
              {isFetching ? "Loading..." : "Try Again"}
            </button>
            <button 
              onClick={handleGoBack}
              className="px-6 py-2 bg-secondary border border-secondary-border text-foreground rounded-lg hover:bg-secondary/90 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasNoQuestions = localQuestions.length === 0;

  return (
    <div className="min-h-screen bg-background p-6">
      {/* Back Button - excluded from PDF */}
      <button
        onClick={handleGoBack}
        className="flex items-center text-muted-foreground hover:text-primary transition-colors mb-4 pdf-export-remove"
      >
        <ArrowLeft className="mr-2" size={20} />
        Back to Dashboard
      </button>
      
      {/* PDF Export Container - includes header and content */}
      <div id="pdf-export-container">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                <User className="text-white" size={20} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground">Interview Questions</h2>
                <p className="text-sm text-muted-foreground">
                  {localQuestions.length > 0 && "Generated report"}
                </p>
              </div>
            </div>
            
            {/* Download PDF Button - excluded from PDF */}
            {localQuestions.length > 0 && (
              <button
                onClick={handleDownloadPDF}
                disabled={isDownloadingPDF}
                className={`px-6 py-2 bg-secondary border border-secondary-border text-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center gap-2 min-w-[140px] justify-center pdf-export-remove ${
                  isDownloadingPDF ? 'opacity-75 cursor-not-allowed' : ''
                }`}
                title={isDownloadingPDF ? "Generating PDF..." : "Download questions as PDF file"}
              >
                {isDownloadingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                    <span className="hidden sm:inline">Downloading...</span>
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    <span className="hidden sm:inline">Download PDF</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Questions Content */}
        <div className="bg-secondary border border-secondary-border rounded-lg">
          {hasNoQuestions ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="text-primary" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">No Questions Found</h3>
              <p className="text-muted-foreground mb-6">
                This user has not submitted any questions yet.
              </p>
              <div className="space-x-4">
                <button 
                  onClick={handleRefetch} 
                  className="px-6 py-2 bg-secondary border border-secondary-border text-foreground rounded-lg hover:bg-secondary/90 transition-colors pdf-export-remove"
                  disabled={isFetching}
                >
                  {isFetching ? "Loading..." : "Refresh"}
                </button>
                <button 
                  onClick={handleGoBack}
                  className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors pdf-export-remove"
                >
                  Back to Dashboard
                </button>
              </div>
            </div>
          ) : (
            <div className="p-6">
              {/* Display draggable questions */}
              <div className="space-y-4">
                {localQuestions.map((question, index) => (
                  <DraggableUserQuestion
                    key={question.dragId || question.id}
                    question={question}
                    index={index}
                    dragProps={getDragProps(index)}
                    isDragging={isDragging(index)}
                    isDraggedOver={isDraggedOver(index)}
                    onMoveUp={() => moveQuestionUp(index)}
                    onMoveDown={() => moveQuestionDown(index)}
                    isFirst={index === 0}
                    isLast={index === localQuestions.length - 1}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 